import { BestFormationFromBackendSquad } from "@/api/privateRequests/squad-builder/build-squad";
import { Solving } from "@/components/organisms/SolutionView/SolveStatus";
import { Button } from "@/design-system/Button/Button";
import {
  ChemistryDelta,
  ChemistryIcon,
} from "@/design-system/Chemistry/Chemistry";
import PlayerCardCondensed from "@/design-system/PlayerCard/PlayerCardCondensed";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  generateNavigationUrl,
  tierSelector,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import {
  bestSquadSelector,
  buildBestSquadStatusSelector,
  resetBestSquad,
  setShowingBestSquad,
  showingBestSquadSelector,
} from "@repo/shared/redux";
import { Chemistry, EvoPathId } from "@repo/shared/types";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";
import { useMediaQuery, useResizeObserver } from "usehooks-ts";

import { EmptySlotCard } from "./EmptySlotCard";
import {
  EmptySlot,
  PitchPlayer,
  PitchSlot,
  SquadSwap,
  SquadView,
  useActiveSquad,
  useBestSquad,
  useSquadSwap,
} from "./useActiveSquad";
import { useOpenInSquadBuilder } from "./useOpenInSquadBuilder";

const PITCH_ART = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/pitch.svg`;

/* The pitch is square, at most 50rem, and shrinks with the screen: the window's
   width, and on desktop its height too, so the whole squad fits in view. */
const PITCH_SIZE = "mx-auto aspect-square w-full";
/* Default limits; a page can pass its own instead (see pitchClassName). */
const PITCH_LIMITS = "max-w-200 mobile:max-w-[min(50rem,calc(100dvh-11rem))]";
const PITCH_MAX = 800;

/* Desktop condensed card scale on a full-size pitch, with its meta rating chip
   under it. Cards shrink with the pitch so a back four and a midfield four
   never touch. */
const CARD_SCALE_AT_MAX = 0.62;
/* Condensed card scale per pitch pixel on phones (≈ a fifth of its height). */
const PHONE_CARD_PER_PX = 0.00072;

/* The chip shrinks slower than the card, so its figure stays readable on a
   smaller pitch; never past its own size. */
const CHIP_PER_CARD_SCALE = 1.7;
/* Nor below this, so its figure stays readable on a phone's pitch. */
const CHIP_MIN_SCALE = 0.55;

/**
 * A pitch card: the card as it plays here, at the slot's position,
 * and under it the meta rating it has in that role with its chemistry.
 */
/** What an evolved card carries, so its page opens evolved. */
type EvolvedPitchPlayer = PitchPlayer & {
  evoPathId?: EvoPathId;
  evolutionPath?: number[];
  pathwayPath?: number[][];
};

const PitchCard = ({ slot, scale }: { slot: PitchSlot; scale: number }) => {
  const tier = useSelector(
    // The tier comes from the 0-chem rating; the chip shows the one it plays at.
    tierSelector(
      slot.metaRatingZeroChem ?? null,
      slot.playerRoleId ?? null,
      true,
    ),
  );
  const chipScale = Math.min(
    1,
    Math.max(CHIP_MIN_SCALE, scale * CHIP_PER_CARD_SCALE),
  );
  // The player's own page (as from My Players), in the role it plays here.
  const { evoPathId, evolutionPath, pathwayPath } =
    slot.player as EvolvedPitchPlayer;
  const playerPage = generateNavigationUrl({
    resourceId: slot.player.resourceId,
    name: slot.player.cardName ?? "",
    playerRoleId: slot.playerRoleId,
    evoPathId,
    evolutionPath,
    pathwayPath,
    basePath: "/players",
  });
  return (
    <Link
      to={playerPage}
      className="flex flex-col items-center rounded-lg transition-transform duration-150 hover:scale-104 focus-visible:outline-2 focus-visible:outline-blue-400"
    >
      <PlayerCardCondensed
        player={{ ...slot.player, cardName: slot.player.cardName ?? "" }}
        position={slot.position}
        scale={scale}
        hidePrice
        firstOwner={slot.player.firstOwner}
        chemistry={slot.chemistry as Chemistry | undefined}
        previousChemistry={slot.previousChemistry as Chemistry | undefined}
        chemistryScale={chipScale}
      />
      <div className="-mt-2 origin-top" style={{ scale: chipScale }}>
        <MetaRatingChip
          metaRating={slot.metaRating as number}
          tier={tier}
          // In a preview, how the swap moved this player's rating.
          metaRatingDelta={
            slot.previousMetaRating != null && slot.metaRating != null
              ? slot.metaRating - slot.previousMetaRating
              : undefined
          }
        />
      </div>
    </Link>
  );
};

/** A position nobody plays in: the empty card, and its position under it. */
const EmptyPitchCard = ({
  slot,
  scale,
}: {
  slot: EmptySlot;
  scale: number;
}) => {
  const { positions } = useSelector(contentLabelsSelector);
  const chipScale = Math.min(
    1,
    Math.max(CHIP_MIN_SCALE, scale * CHIP_PER_CARD_SCALE),
  );
  return (
    <div className="flex flex-col items-center">
      <EmptySlotCard scale={scale} />
      <div className="-mt-2 origin-top" style={{ scale: chipScale }}>
        <span className="flex h-6 items-center rounded-full border border-white/10 bg-neutral-950/70 px-2.5 text-xs font-semibold text-foreground-muted">
          {positions[slot.position]?.name ?? slot.position}
        </span>
      </div>
    </div>
  );
};

/**
 * The squad's meta rating and chemistry. Beside the heading rather than on the
 * pitch, where it covered the front line.
 */
const SquadStats = ({
  squad,
  best,
  baseMetaRating,
  baseChemistry,
}: {
  squad: SquadView;
  /** Showing a preview or the best squad: print the change too. */
  best: boolean;
  /** The active squad's figures, so a preview or the best squad shows the change. */
  baseMetaRating?: number;
  baseChemistry?: number;
}) => {
  const { t } = useTranslation("myClub");
  if (squad.metaRating == null && squad.chemistry == null) return null;
  return (
    <div className="flex shrink-0 flex-col items-end gap-y-1.5">
      {squad.chemistry != null && (
        <div className="flex items-center justify-end gap-x-2">
          <span className="text-xs tracking-wider whitespace-nowrap text-foreground-muted uppercase max-mobile:hidden">
            {t("home.squad.chemistry")}
          </span>
          <span className="flex items-center gap-1.5 text-sm font-semibold tabular-nums">
            <ChemistryIcon chemistry={3} className="h-3.5 w-4" />
            <span>{squad.chemistry}</span>
            {best && baseChemistry != null && (
              <ChemistryDelta value={squad.chemistry - baseChemistry} />
            )}
          </span>
        </div>
      )}
      {squad.metaRating != null && (
        <div className="flex items-center justify-end gap-x-2">
          <span className="text-xs tracking-wider whitespace-nowrap text-foreground-muted uppercase max-mobile:hidden">
            {t("home.squad.metaRating")}
          </span>
          <MetaRatingChip
            metaRating={squad.metaRating}
            metaRatingDelta={
              best && baseMetaRating != null
                ? squad.metaRating - baseMetaRating
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

const Pitch = ({
  squad,
  isMobile,
  best = false,
  note,
  corner,
  sizeClassName,
  showSquadBuilderLink,
  onOpenSquadBuilder,
  openingSquadBuilder,
  squadBuilderLabel,
}: {
  squad: SquadView;
  isMobile: boolean;
  sizeClassName?: string;
  showSquadBuilderLink: boolean;
  onOpenSquadBuilder: () => void;
  openingSquadBuilder: boolean;
  squadBuilderLabel: string;
  /** Showing the best possible squad rather than the active one. */
  best?: boolean;
  note?: string;
  /** Pinned top-right on the pitch, so showing it never moves the page. */
  corner?: ReactNode;
}) => {
  const { t } = useTranslation("myClub");
  const ref = useRef<HTMLDivElement>(null);
  const { width = PITCH_MAX } = useResizeObserver({ ref, box: "border-box" });
  /* Desktop scales the cards with the pitch from their full-size scale. On
     phones the pitch is small, so the cards are sized to the pitch itself:
     a card about a fifth of its height, so five lines don't pile up. */
  const ys = [...squad.slots, ...squad.emptySlots].map((slot) => slot.y);
  const topY = Math.min(...ys);
  const spanY = Math.max(...ys) - topY;
  const rowY = (y: number) => (spanY > 0 ? (y - topY) / spanY : 0.5);
  const incomingId = squad.slots.find((slot) => slot.highlighted)?.player
    .resourceId;
  const cardScale = isMobile
    ? width * PHONE_CARD_PER_PX
    : (CARD_SCALE_AT_MAX * width) / PITCH_MAX;

  return (
    <div
      ref={ref}
      className={cn(
        PITCH_SIZE,
        sizeClassName ?? PITCH_LIMITS,
        "relative overflow-hidden rounded-2xl border bg-neutral-900 transition-colors duration-200 mobile:rounded-2xl",
        best ? "border-blue-800/70" : "border-neutral-700",
      )}
    >
      <img
        src={PITCH_ART}
        alt=""
        className="absolute inset-0 size-full object-cover opacity-90"
      />
      {corner && (
        <div className="absolute top-3 right-3 z-30 flex flex-col items-end gap-2 mobile:top-4 mobile:right-4">
          {corner}
        </div>
      )}
      <div className="absolute inset-0">
        {/* Rows stretch from the front line at the top of the inset to the
            keeper at its bottom, so every formation uses the whole pitch.
            Inset so the front line clears the chip and the keeper the footer. */}
        <div
          /* Fixed whatever sits in the corner: showing the toggle or a
             preview must never move the players. */
          /* The chip and toggle keep their size as the pitch shrinks, so the
             front line also keeps a floor below them. */
          className="absolute inset-x-[4%] top-[max(22%,5.5rem)] bottom-[12%] mobile:top-[max(13%,5rem)] mobile:bottom-[12%]"
        >
          {squad.emptySlots.map((slot) => (
            <div
              key={`empty-${slot.index}`}
              className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${slot.x * 100}%`,
                top: `${rowY(slot.y) * 100}%`,
              }}
            >
              <EmptyPitchCard slot={slot} scale={cardScale} />
            </div>
          ))}
          {squad.slots.map((slot) => {
            /* A player the swap changes (chemistry or meta rating) nods
               along once; only the incoming one keeps its glow. */
            const changed =
              !slot.highlighted &&
              ((slot.previousChemistry != null &&
                slot.previousChemistry !== slot.chemistry) ||
                (slot.previousMetaRating != null &&
                  slot.metaRating != null &&
                  Math.abs(slot.metaRating - slot.previousMetaRating) >= 0.05));
            return (
              <div
                // A new key per incoming player replays the animations on every
                // swap.
                key={`${slot.index}-${slot.player.resourceId}-${changed ? incomingId : ""}`}
                className={cn(
                  "absolute -translate-x-1/2 -translate-y-1/2",
                  slot.highlighted ? "z-20" : "z-10",
                )}
                style={{
                  left: `${slot.x * 100}%`,
                  top: `${rowY(slot.y) * 100}%`,
                }}
              >
                <div
                  className={cn(
                    slot.highlighted &&
                      "drop-shadow-lg drop-shadow-blue-300/80 motion-safe:animate-preview-pop",
                    changed && "motion-safe:animate-preview-nudge",
                  )}
                >
                  <PitchCard slot={slot} scale={cardScale} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {(note || showSquadBuilderLink) && (
        <div className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-3.5 bg-linear-to-t from-neutral-950/95 via-neutral-950/50 via-65% to-transparent px-4 py-4 max-mobile:hidden">
          {note && (
            <span className="text-xs whitespace-nowrap text-neutral-300">
              {note}
            </span>
          )}
          {showSquadBuilderLink && (
            <Button
              variant="secondary"
              size="small"
              className="ml-auto"
              disabled={openingSquadBuilder}
              onClick={onOpenSquadBuilder}
            >
              {squadBuilderLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const PitchSkeleton = ({ sizeClassName }: { sizeClassName?: string }) => (
  <div
    className={cn(
      PITCH_SIZE,
      sizeClassName ?? PITCH_LIMITS,
      "animate-pulse rounded-2xl border border-neutral-700 bg-neutral-900 mobile:rounded-2xl",
    )}
  />
);

/**
 * The top of the club home: the user's active squad on a pitch, so everything
 * suggested further down reads as a change to that squad.
 */
/** An upgrade tried out on the pitch: one player swapped for another. */
export type SquadPreview = SquadSwap & { onClear: () => void };

export const ActiveSquadSection = ({
  enabled,
  id,
  preview,
  className,
  showBestSquad = true,
  showSquadBuilderLink = true,
  pitchClassName,
}: {
  enabled: boolean;
  id?: string;
  className?: string;
  /** The one-time best-squad reveal belongs to the club home only. */
  showBestSquad?: boolean;
  showSquadBuilderLink?: boolean;
  /** Overrides the pitch's size limits, e.g. to leave room below it. */
  pitchClassName?: string;
  /** Shows the active squad with this upgrade swapped in. */
  preview?: SquadPreview;
}) => {
  const { t } = useTranslation(["myClub", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const squadBuilder = useOpenInSquadBuilder();
  const { squad: active, source, status } = useActiveSquad({ enabled });
  const swap = useSquadSwap(source, active);
  const best = useBestSquad({ name: t("home.squad.best") });
  const bestSquadData = useSelector(bestSquadSelector);
  const bestStatus = useSelector(buildBestSquadStatusSelector);
  // "Show" on the onboarding's best squad step puts it here; see TryNext.
  const showBest = useSelector(showingBestSquadSelector);

  /* A failed build puts the best squad away; the step says so. */
  useEffect(() => {
    if (bestStatus === "rejected") dispatch(resetBestSquad());
  }, [bestStatus, dispatch]);

  const previewed = useMemo(() => preview && swap(preview), [preview, swap]);
  // Names the previewed player, so it is clear the swap comes along.
  const squadBuilderLabel =
    previewed && preview?.inPlayer.cardName
      ? t("home.squad.squadBuilderCtaWith", { name: preview.inPlayer.cardName })
      : t("home.squad.squadBuilderCta");
  const showingBest =
    showBestSquad &&
    !previewed &&
    showBest &&
    bestStatus === "fulfilled" &&
    !!best;
  const squad = previewed ?? (showingBest ? best : active);
  /*
    The squad builder opens on what the pitch shows: with a preview, the swap
    made; with the best squad, the best squad; else the active squad.
  */
  const openSquadBuilder = () =>
    previewed && preview
      ? squadBuilder.open(preview, source?.playerRoleIds[preview.slotIndex])
      : showingBest && bestSquadData
        ? // Same endpoint as the builder's own: the full squad is in there.
          squadBuilder.openBest(
            bestSquadData as unknown as BestFormationFromBackendSquad,
          )
        : squadBuilder.open();
  // Both a preview and the best squad are read against the active squad.
  const comparing = !!previewed || showingBest;
  const note = showingBest ? t("home.squad.bestNote") : undefined;

  const meta = [squad?.formationLabel].filter(Boolean);

  // On phones the card's own Previewing button takes it off again.
  const corner = previewed ? (
    isMobile ? null : (
      <Button variant="secondary" size="small" onClick={preview?.onClear}>
        {t("recs.clearPreview")}
      </Button>
    )
  ) : showingBest ? (
    <Button
      variant="secondary"
      size="small"
      onClick={() => dispatch(setShowingBestSquad(false))}
    >
      {t("home.squad.backToActive")}
    </Button>
  ) : showBestSquad && bestStatus === "pending" ? (
    // Built from the onboarding step: the pitch says it is on its way.
    <div className="max-w-56 rounded-lg border border-white/10 bg-neutral-950/70 px-2.5 py-1.5 backdrop-blur-md mobile:max-w-none">
      <Solving
        label={t("home.squad.building")}
        textClassName="text-xs leading-snug mobile:whitespace-nowrap"
      />
    </div>
  ) : undefined;

  return (
    <section
      id={id}
      className={cn(
        "flex scroll-mt-24 flex-col gap-3 mobile:gap-3.5",
        className,
      )}
    >
      <div className="flex items-end gap-3 mobile:gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1 px-0.5 mobile:px-0">
          <span
            className={cn(
              "text-xs font-bold tracking-widest uppercase",
              comparing ? "text-blue-300" : "text-neutral-400",
            )}
          >
            {previewed
              ? t("recs.preview")
              : showingBest
                ? t("home.squad.best")
                : t("home.squad.active")}
          </span>
          {/* The best squad's name only repeats the label: its formation is
              all it adds. Same heading box either way, so toggling never
              moves the pitch. */}
          <h2 className="text-lg leading-tight font-semibold mobile:text-xl">
            {!showingBest && (
              <span>{squad?.name || t("home.squad.active")} </span>
            )}
            {meta.length > 0 && (
              <span className="text-sm font-normal text-foreground-muted mobile:text-sm">
                <span>
                  {showingBest ? meta.join(" · ") : `· ${meta.join(" · ")}`}
                </span>
              </span>
            )}
          </h2>
        </div>
        {squad && squad.slots.length > 0 && (
          <SquadStats
            squad={squad}
            best={comparing}
            baseMetaRating={active?.metaRating}
            baseChemistry={active?.chemistry}
          />
        )}
      </div>

      {squad && squad.slots.length > 0 ? (
        <Pitch
          squad={squad}
          isMobile={isMobile}
          best={comparing}
          note={note}
          corner={corner || undefined}
          sizeClassName={pitchClassName}
          showSquadBuilderLink={showSquadBuilderLink}
          onOpenSquadBuilder={openSquadBuilder}
          openingSquadBuilder={squadBuilder.opening}
          squadBuilderLabel={squadBuilderLabel}
        />
      ) : status === "rejected" ||
        (status === "fulfilled" && !squad?.slots.length) ? (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-neutral-700 bg-neutral-900 px-6 text-center text-sm text-foreground-muted">
          {t("home.squad.empty")}
        </div>
      ) : (
        <PitchSkeleton sizeClassName={pitchClassName} />
      )}

      {isMobile && note && (
        <span className="px-0.5 text-xs text-foreground-muted">{note}</span>
      )}
      {/* On phones a quiet link under the pitch's bottom right, not a
          full-width button competing with the squad. */}
      {isMobile && showSquadBuilderLink && (
        <Button
          variant="subtle"
          size="small"
          className="-mt-1 -mr-3 self-end"
          disabled={squadBuilder.opening}
          onClick={openSquadBuilder}
        >
          {squadBuilderLabel}
        </Button>
      )}
    </section>
  );
};
